import React, { useState, useEffect } from 'react';
import { settingsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { FileSpreadsheet, Save, Loader2, Eye, EyeOff, ListChecks } from 'lucide-react';
import { toast } from 'sonner';
import { fehlertext } from '../lib/fehlertext';

function DiagResult({ diag }) {
  if (!diag) return null;
  var conn = diag.connection;
  return (
    <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm space-y-1" data-testid="lexware-diag-result">
      <div className={conn && conn.ok ? 'text-green-700' : 'text-red-700'}>
        {conn && conn.ok
          ? '\u2713 Verbindung OK' + (conn.company ? ' (' + conn.company + ')' : '')
          : '\u2717 Verbindung fehlgeschlagen: ' + (conn ? (conn.error || ('HTTP ' + conn.status)) : 'unbekannt')}
      </div>
      {diag.timesheet && <TimesheetDiag ts={diag.timesheet} />}
    </div>
  );
}

function TimesheetDiag({ ts }) {
  if (ts.found === false) return <div className="text-red-700">{'\u2717'} {ts.blocker}</div>;
  return (
    <div className="space-y-0.5 text-slate-700">
      <div>Mitarbeiter: {ts.employee || '\u2013'}</div>
      <div>Satz: {ts.satz != null ? ts.satz + ' \u20AC' : '\u2014 fehlt'} · Qualifikation: {ts.qualifikation || '\u2014'}</div>
      <div>Jugendamt: {ts.jugendamt || '\u2014 fehlt'} · Kundennummer: {ts.kundennummer || '\u2014'}</div>
      <div>Kontakt in Lexware: {ts.contact_found === true ? '\u2713 gefunden' : ts.contact_found === false ? '\u2717 nicht gefunden' : '\u2014'}</div>
      <div>Abrechenbare Stunden: {ts.abrechenbar}</div>
      <div className={ts.would_succeed ? 'text-green-700 font-medium' : 'text-red-700 font-medium'}>
        {ts.would_succeed ? '\u2713 Rechnung würde erstellt werden' : '\u2717 Blocker: ' + (ts.blocker || 'unbekannt')}
      </div>
      {ts.created_invoice_id && (
        <div className="text-green-700 font-medium">
          {'\u2713'} Entwurf in Lexware erstellt (ID: {ts.created_invoice_id}) — zu finden unter „Belege → Entwürfe"
        </div>
      )}
      {ts.create_error && (
        <div className="text-red-700 font-medium">{'\u2717'} Fehler bei der Erstellung: {ts.create_error}</div>
      )}
    </div>
  );
}

var STATUS_REIHENFOLGE = ['draft', 'open', 'paid', 'paidoff', 'overdue', 'voided'];
var STATUS_LABEL = {
  draft: 'Entwurf', open: 'offen', paid: 'bezahlt',
  paidoff: 'ausgeglichen', overdue: 'überfällig', voided: 'storniert',
};

function BelegBericht({ daten }) {
  if (!daten) return null;
  var b = daten.report;
  if (daten.running) {
    return (
      <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-sm text-blue-800" data-testid="voucher-report-running">
        Der Bericht läuft gerade. Das kann einige Minuten dauern — später erneut abrufen.
      </div>
    );
  }
  if (!b) {
    return <p className="text-sm text-slate-500" data-testid="voucher-report-empty">Noch kein Bericht vorhanden.</p>;
  }
  if (b.fehler) {
    return <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-800" data-testid="voucher-report-error">{b.fehler}</div>;
  }
  var v = b.status_verteilung || {};
  var z = b.zuordnung || {};
  var vs = b.vollstaendigkeit || {};
  var unbekannt = Object.keys(b.unbekannte_statuswerte || {});
  var fehlende = vs.fehlende_status || [];
  var nichtGefunden = b.nicht_gefunden || [];
  var doppelte = b.doppelte_beleg_ids || [];
  var statusZeilen = [];
  var i;
  for (i = 0; i < STATUS_REIHENFOLGE.length; i++) {
    var s = STATUS_REIHENFOLGE[i];
    statusZeilen.push({ key: s, label: STATUS_LABEL[s], wert: v[s] != null ? v[s] : 0 });
  }
  var doppelteText = '';
  for (i = 0; i < doppelte.length; i++) {
    doppelteText = doppelteText + (i ? ', ' : '') + doppelte[i].beleg_id;
  }
  var unbekanntText = '';
  for (i = 0; i < unbekannt.length; i++) {
    unbekanntText = unbekanntText + (i ? ', ' : '') + unbekannt[i] + ' (' + b.unbekannte_statuswerte[unbekannt[i]] + ')';
  }
  var kopf = b.konto || {};
  var erstellt = String(b.erstellt_am || '').replace('T', ' ').slice(0, 16);
  var g = b.geschrieben || {};
  var nurVorbeleg = b.nur_vorbeleg || [];

  return (
    <div className="space-y-3 text-sm" data-testid="voucher-report-result">
      <div className={b.schreibend ? 'rounded-lg border-2 border-slate-400 bg-slate-100 p-2 text-xs font-medium text-slate-800' : 'rounded-lg bg-slate-50 p-2 text-xs text-slate-600'}
        data-testid="voucher-report-mode">
        {b.schreibend
          ? ('Schreibender Lauf \u00b7 neu gesetzt: ' + (g.gesetzt || 0) + ' \u00b7 gewechselt: ' + (g.gewechselt || 0)
             + ' \u00b7 übersprungen: ' + (g.uebersprungen || 0) + ' \u00b7 vermisst: ' + (g.vermisst || 0))
          : 'Nur gelesen \u2014 an den Stundenzetteln wurde nichts geändert.'}
      </div>

      {b.schreib_abbruch && (
        <div className="rounded-lg bg-red-50 border-2 border-red-300 p-3 text-red-800" data-testid="voucher-report-write-abort">
          {b.schreib_abbruch}
        </div>
      )}
      <div className="rounded-lg border-2 border-blue-300 bg-blue-50 p-3" data-testid="voucher-report-account">
        <div className="text-base font-semibold text-blue-900">
          Lexware-Konto: {kopf.companyName || '\u2014'}
        </div>
        <div className="text-xs text-blue-800 mt-0.5">
          organizationId: {kopf.organizationId || '\u2014'}
        </div>
        <div className="text-xs text-blue-900 mt-1 font-medium">{kopf.hinweis || ''}</div>
      </div>

      {!vs.vollstaendig && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-amber-900" data-testid="voucher-report-incomplete">
          <div className="font-medium">{vs.warnung}</div>
          {fehlende.map(function(f, k) { return <div key={k} className="text-xs">{'Ausgefallen \u2014 ' + f}</div>; })}
        </div>
      )}

      <div data-testid="voucher-report-distribution">
        <p className="font-medium text-slate-800">Zustand unserer Belege in Lexware</p>
        <ul className="mt-1 space-y-0.5 text-slate-700">
          {statusZeilen.map(function(row) {
            return <li key={row.key}>{row.label}: <strong>{row.wert}</strong></li>;
          })}
        </ul>
      </div>

      <div className="text-slate-700" data-testid="voucher-report-quote">
        {'Stundenzettel mit Beleg-ID: ' + (z.zettel_mit_beleg_id != null ? z.zettel_mit_beleg_id : 0)
          + ' \u2014 in Lexware gefunden: ' + (z.gefunden != null ? z.gefunden : 0)
          + ', nicht gefunden: ' + (z.nicht_gefunden != null ? z.nicht_gefunden : 0)}
      </div>

      {nichtGefunden.length > 0 && (
        <details data-testid="voucher-report-missing">
          <summary className="cursor-pointer text-slate-600">{'Nicht gefundene Belege (' + nichtGefunden.length + ')'}</summary>
          <ul className="mt-1 text-xs text-slate-600 space-y-1.5">
            {nichtGefunden.map(function(n, k) {
              return (
                <li key={k} className="border-l-2 border-slate-200 pl-2">
                  <div>{(n.kind || '\u2014') + ' \u00b7 AZ: ' + (n.aktenzeichen || '\u2014')}</div>
                  <div>{'Jugendamt: ' + (n.jugendamt || '\u2014') + ' \u00b7 Kundennummer: ' + (n.kundennummer || '\u2014')}</div>
                  <div className="text-slate-400">{n.monat + ' \u00b7 ' + n.mitarbeiter + ' \u00b7 Beleg ' + n.beleg_id}</div>
                </li>
              );
            })}
          </ul>
        </details>
      )}

      {doppelte.length > 0 && (
        <div className="text-red-700" data-testid="voucher-report-duplicates">
          {'Doppelte Beleg-IDs: ' + doppelteText}
        </div>
      )}

      {unbekannt.length > 0 && (
        <div className="text-amber-800" data-testid="voucher-report-unknown">
          {'Unbekannte Statuswerte: ' + unbekanntText}
        </div>
      )}

      <p className="text-xs text-slate-400">
        {'Erstellt am ' + erstellt + ' \u00b7 Laufzeit ' + vs.laufzeit_sekunden + ' s'
          + (vs.vollstaendig ? ' \u00b7 alle sechs Abfragen gelangen' : '')
          + (nurVorbeleg.length ? ' \u00b7 ' + nurVorbeleg.length + ' Zettel nur mit Vorbeleg (nicht erfasst)' : '')}
      </p>
    </div>
  );
}

export function LexwareSettings() {
  var [apiKey, setApiKey] = useState('');
  var [autoEnabled, setAutoEnabled] = useState(false);
  var [saving, setSaving] = useState(false);
  var [showKey, setShowKey] = useState(false);
  var [loaded, setLoaded] = useState(false);
  var [diag, setDiag] = useState(null);
  var [diagLoading, setDiagLoading] = useState(false);
  var [diagTsId, setDiagTsId] = useState('');
  var [creating, setCreating] = useState(false);
  var [bericht, setBericht] = useState(null);
  var [berichtLaden, setBerichtLaden] = useState(false);
  var [syncEnabled, setSyncEnabled] = useState(false);
  var [orgId, setOrgId] = useState('');

  useEffect(function() {
    settingsAPI.getLexware().then(function(res) {
      var d = res.data || {};
      setApiKey(d.api_key || '');
      setAutoEnabled(d.auto_invoice_enabled || false);
      setSyncEnabled(d.voucher_sync_enabled || false);
      setLoaded(true);
    }).catch(function() { setLoaded(true); });
  }, []);

  function handleSave() {
    setSaving(true);
    // Alle Felder senden: update_lexware_settings schreibt config.model_dump() per $set,
    // ein fehlendes Feld würde still auf den Pydantic-Default zurückfallen.
    settingsAPI.updateLexware({ api_key: apiKey, auto_invoice_enabled: autoEnabled,
                                voucher_sync_enabled: syncEnabled })
      .then(function() { toast.success('Lexware-Einstellungen gespeichert'); })
      .catch(function(e) { toast.error(fehlertext(e, 'Fehler beim Speichern')); })
      .finally(function() { setSaving(false); });
  }

  function runDiagnose(withTimesheet) {
    setDiagLoading(true);
    settingsAPI.lexwareDiagnose(withTimesheet ? diagTsId.trim() : null, false)
      .then(function(res) { setDiag(res.data); })
      .catch(function() { toast.error('Diagnose fehlgeschlagen'); })
      .finally(function() { setDiagLoading(false); });
  }

  if (!loaded) return null;

  function berichtHolen() {
    setBerichtLaden(true);
    settingsAPI.getLexwareVoucherReport()
      .then(function(res) { setBericht(res.data); })
      .catch(function(e) { toast.error(fehlertext(e, 'Bericht konnte nicht geladen werden')); })
      .finally(function() { setBerichtLaden(false); });
  }

  function berichtStarten(schreiben) {
    setBerichtLaden(true);
    settingsAPI.startLexwareVoucherReport(
      schreiben ? { schreiben: true, bestaetigte_organization_id: orgId.trim() } : { schreiben: false })
      .then(function() {
        toast.success(schreiben
          ? 'Schreibender Belegabgleich gestartet. Das kann einige Minuten dauern.'
          : 'Belegstatus-Bericht gestartet. Das kann einige Minuten dauern.');
        setBericht({ running: true, report: null });
      })
      .catch(function(e) { toast.error(fehlertext(e, 'Bericht konnte nicht gestartet werden')); })
      .finally(function() { setBerichtLaden(false); });
  }

  return (
    <Card data-testid="lexware-settings">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <FileSpreadsheet className="h-5 w-5" />
          Lexware Office
        </CardTitle>
        <CardDescription>
          Automatische Rechnungserstellung als Entwurf in Lexware Office beim Einreichen eines Stundenzettels
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="max-w-md">
          <Label className="text-sm">Lexware API-Key</Label>
          <div className="relative">
            <Input
              type={showKey ? 'text' : 'password'}
              value={apiKey}
              onChange={function(e) { setApiKey(e.target.value); }}
              placeholder={apiKey === '********' ? '\u2022\u2022\u2022\u2022 gespeichert' : 'API-Key eingeben'}
              className="pr-10"
              data-testid="lexware-api-key-input"
            />
            <button type="button" onClick={function() { setShowKey(!showKey); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
              {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Switch checked={autoEnabled} onCheckedChange={setAutoEnabled} data-testid="lexware-auto-invoice-toggle" />
          <Label className="text-sm">Automatische Lexware-Rechnung aktiv</Label>
        </div>

        <p className="text-xs text-slate-400">
          Wenn aktiviert, wird beim Einreichen eines Stundenzettels automatisch ein Rechnungs-Entwurf in Lexware Office erstellt.
          Die Rechnung wird nicht finalisiert oder versendet — sie muss manuell geprüft und freigegeben werden.
        </p>

        <div className="flex items-center gap-3">
          <Switch checked={syncEnabled} onCheckedChange={setSyncEnabled} data-testid="lexware-voucher-sync-toggle" />
          <Label className="text-sm">Belegabgleich darf schreiben (Belegstatus am Stundenzettel speichern)</Label>
        </div>

        <p className="text-xs text-slate-400">
          Wenn aktiviert, darf der schreibende Belegabgleich unten den in Lexware ermittelten Belegstatus
          am Stundenzettel speichern. Der lesende Bericht und das Zurücksetzen laufen unabhängig davon.
        </p>

        <Button onClick={handleSave} disabled={saving || !loaded} className="bg-blue-800 hover:bg-blue-900" data-testid="save-lexware-settings">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Speichern
        </Button>

        {/* Belegstatus-Bericht (rein lesend) — bewusst getrennt vom Diagnose-Bereich unten */}
        <div className="mt-4 rounded-lg border border-slate-300 bg-white p-4 space-y-3" data-testid="voucher-report-panel">
          <p className="text-sm font-medium text-slate-800 flex items-center gap-2">
            <ListChecks className="h-4 w-4" />
            Belegstatus in Lexware (nur Abfrage)
          </p>
          <p className="text-xs text-slate-500">
            Fragt bei Lexware ab, in welchem Zustand unsere Belege dort sind — Entwurf, offen, bezahlt, storniert.
            Der Bericht ändert nichts: er legt keinen Beleg an, verschickt nichts und rührt keinen Stundenzettel an.
            Der Lauf dauert je nach Belegmenge einige Minuten.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" onClick={function() { berichtStarten(false); }} disabled={berichtLaden}
              className="bg-blue-800 hover:bg-blue-900" data-testid="voucher-report-start">
              {berichtLaden ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Belegstatus abrufen
            </Button>
            <Button type="button" variant="outline" onClick={berichtHolen} disabled={berichtLaden}
              data-testid="voucher-report-load">
              Ergebnis anzeigen
            </Button>
          </div>
          <BelegBericht daten={bericht} />

          {/* Schreibender Abgleich — bewusst unterhalb des lesenden Berichts */}
          <div className="mt-3 border-t-2 border-slate-200 pt-3 space-y-2" data-testid="voucher-sync-panel">
            <p className="text-sm font-medium text-slate-800">Belegstatus am Stundenzettel speichern</p>
            <p className="text-xs text-slate-500">
              Vergleichen Sie den Firmennamen oben mit dem Konto, in dem Sie in Lexware angemeldet sind,
              und tragen Sie dann die organizationId ein. Gespeichert werden nur der Belegstatus, die
              Rechnungsnummer und der Prüfzeitpunkt — kein Häkchen, keine E-Mail, kein Beleg in Lexware.
            </p>
            <Input placeholder="organizationId aus dem Bericht oben" value={orgId}
              onChange={function(e) { setOrgId(e.target.value); }}
              className="max-w-md" data-testid="voucher-sync-org-input" />
            <Button type="button" onClick={function() { berichtStarten(true); }}
              disabled={berichtLaden || !orgId.trim()}
              className="bg-slate-800 hover:bg-slate-900" data-testid="voucher-sync-start">
              {berichtLaden ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Status speichern (schreibend)
            </Button>
            {!syncEnabled && (
              <p className="text-xs text-amber-700" data-testid="voucher-sync-disabled-hint">
                Der Schalter oben ist aus — der schreibende Lauf wird abgewiesen.
              </p>
            )}
          </div>
        </div>

        {/* Diagnose Panel */}
        <div className="mt-4 border-t border-slate-200 pt-4 space-y-3">
          <p className="text-sm font-medium text-slate-800">Diagnose</p>
          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" variant="outline" onClick={function() { runDiagnose(false); }} disabled={diagLoading} data-testid="lexware-test-connection">
              {diagLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Verbindung testen
            </Button>
            <Input
              placeholder="Stundenzettel-ID (aus der URL)"
              value={diagTsId}
              onChange={function(e) { setDiagTsId(e.target.value); }}
              className="w-72"
              data-testid="lexware-diag-tsid"
            />
            <Button type="button" variant="outline" onClick={function() { runDiagnose(true); }} disabled={diagLoading || !diagTsId.trim()} data-testid="lexware-test-timesheet">
              Stundenzettel prüfen
            </Button>
            <Button type="button" variant="outline"
              className="border-orange-300 text-orange-700 hover:bg-orange-50"
              onClick={function() {
                setCreating(true);
                settingsAPI.lexwareDiagnose(diagTsId.trim(), true)
                  .then(function(res) { setDiag(res.data); })
                  .catch(function() { toast.error('Erstellung fehlgeschlagen'); })
                  .finally(function() { setCreating(false); });
              }} disabled={creating || !diagTsId.trim()}
              data-testid="lexware-create-draft">
              {creating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Test-Entwurf erstellen
            </Button>
          </div>

          <DiagResult diag={diag} />

          <p className="text-xs text-slate-400">
            „Stundenzettel prüfen" ist ein Probelauf (erstellt nichts). „Test-Entwurf erstellen" legt <strong>wirklich</strong> einen Rechnungs-Entwurf in Lexware an. Die Stundenzettel-ID steht in der Adresszeile der Stundenzettel-Seite.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
